import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import LightSelect from "../../pages/CompanyView/LightSelect";
import { WINDOWS } from "../../helpers/scopedWindows";

/* Same list ScopeWindow's own "Date range" field offers (Basics → Workspaces),
   minus Custom — a typed from/to range needs more room than a popover this
   size should try to hold; that stays a Basics-only control. Picking a period
   here writes the SAME `window` field, so the two controls can never disagree
   — this is just a faster way to reach the one setting from where you're
   already looking at the number. */
const PERIOD_OPTIONS = [{ id: "follow", label: "Match the dashboard" }, ...WINDOWS.filter((w) => w.id !== "custom")];

/**
 * Inline "Set Goal" / "Edit Goal" editor — portaled to <body> and anchored
 * under the trigger, same recipe as Labor's PayPopover (colours hard-coded
 * here for the same reason: a portal sits outside any card's own CSS scope —
 * .gmbw-btn / .gmbw-primary are the app-wide tokens, not .gprog's own, so
 * they still resolve correctly here). Commits the goal amount on Enter, Save,
 * or clicking outside; Escape or Cancel discards it. The period picker writes
 * immediately, same as any other quick-edit control on a card.
 */
const GoalPopover = ({ anchor, value, kind, window: win, onValueChange, onWindowChange, onCommit, onCancel }) => {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onCancel();
      else if (e.key === "Enter") onCommit();
    };
    const onPointerDown = (e) => {
      const insideRoot = rootRef.current && rootRef.current.contains(e.target);
      /* LightSelect's own open menu is ALSO portaled straight to <body> — a
         sibling in the DOM, not a descendant of rootRef, even though it's a
         React child of this popover. Without this, opening the period
         dropdown and picking an option looked exactly like a click outside:
         it fired onCommit and tore this popover (and the still-open menu)
         down before the click ever reached the option. */
      const insideMenu = e.target.closest?.("[data-lightselect-menu]");
      if (!insideRoot && !insideMenu) onCommit();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCommit, onCancel]);

  const style = useMemo(() => {
    if (!anchor) return null;
    const W = 252;
    const H = onWindowChange ? 234 : 150;
    const GAP = 8;
    const openAbove = anchor.top > H + GAP + 12 && window.innerHeight - anchor.bottom < H + GAP;
    const top = openAbove ? anchor.top - H - GAP : Math.min(anchor.bottom + GAP, window.innerHeight - H - 12);
    const left = Math.max(12, Math.min(anchor.left, window.innerWidth - W - 12));
    return { top, left, width: W };
  }, [anchor, onWindowChange]);

  if (!style) return null;

  const prefix = kind === "$" ? "$" : null;
  const suffix = kind === "%" ? "%" : null;
  let targetLabel = "Target";
  if (kind === "$") targetLabel = "Target ($)";
  else if (kind === "%") targetLabel = "Target (%)";

  return createPortal(
    <div ref={rootRef} className={`gprog-pop${shown ? " in" : ""}`} style={{ top: style.top, left: style.left, width: style.width }}>
      <div className="gprog-pop-h">Goal</div>

      {onWindowChange && (
        <div className="gprog-pop-fld">
          <span>For</span>
          <LightSelect
            width="100%"
            height={36}
            fontSize={12.5}
            radius={8}
            menuWidth={336}
            value={win || "follow"}
            options={PERIOD_OPTIONS.map((o) => ({ value: o.id, label: o.label }))}
            onChange={(o) => onWindowChange(o.value)}
          />
        </div>
      )}

      <label className="gprog-pop-fld">
        <span>{targetLabel}</span>
        <span className="gprog-pop-box">
          {prefix && <i>{prefix}</i>}
          <input
            ref={inputRef}
            type="number"
            min="0"
            step="any"
            inputMode="decimal"
            placeholder="0"
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            /* Belt-and-suspenders, same reason LightSelect sets this inline
               (see that file) rather than trusting a stylesheet rule: this
               portals to <body>, outside .gprog's own scope, and the app's
               own default body font is Roboto (MUI), not DM Sans — an
               inherited font here is a guess, not a fact. */
            style={{ fontFamily: "DM Sans" }}
          />
          {suffix && <i>{suffix}</i>}
        </span>
      </label>

      <div className="gprog-pop-ft">
        <button type="button" className="gmbw-btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="gmbw-btn primary" onClick={onCommit}>Save</button>
      </div>
    </div>,
    document.body,
  );
};

GoalPopover.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  anchor: PropTypes.object,
  value: PropTypes.string.isRequired,
  kind: PropTypes.oneOf(["$", "%", "#"]),
  window: PropTypes.string,
  onValueChange: PropTypes.func.isRequired,
  onWindowChange: PropTypes.func,
  onCommit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
GoalPopover.defaultProps = { anchor: null, kind: "#", window: "follow", onWindowChange: null };

export default GoalPopover;
